import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import EventCover from "@/src/components/events/event-details/event-cover";
import EventDetailInfo from "@/src/components/events/event-details/event-info";
import EventDetailLocation from "@/src/components/events/event-details/event-location-map";
import EventParticipants from "@/src/components/events/event-details/event-participants";
import EventDetailPhotos from "@/src/components/events/event-details/event-photos";
import AttachmentViewer, {
  Attachment,
} from "@/src/components/ui-kit/attachment-viewer";
import { useAuth } from "@/src/contexts/auth.context";
import { useEventArchive } from "@/src/hooks/features/events/use-event-archive.hooks";
import { useEventRSVP } from "@/src/hooks/features/events/use-event-rsvp.hook";
import { EventItem, RSVPStatus } from "@/src/models/event.model";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import {
  useDeleteEventMutation,
  useGetRSVPsQuery,
} from "@/src/services/api/events/events.hooks";
import { isImageFile } from "@/src/utils/attachment.utils";
import { router } from "expo-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, View } from "react-native";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
type EventDetailProps = Readonly<{
  event: EventItem;
  onBack: () => void;
}>;

const MAX_VISIBLE_AVATARS = 3;

const EventDetail = ({ event, onBack }: EventDetailProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showParticipants, setShowParticipants] = useState(false);

  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);
  const { data: categoriesData } = useGetCategoriesQuery({
    tag: "events",
    pageSize: 1000,
  });

  const { data: rsvpsData } = useGetRSVPsQuery(event.ItemId, RSVPStatus.GOING);

  const {
    userRsvp,
    isAttending,
    isWaitlisted,
    isJoining,
    isLeaving,
    joinButtonText,
    handleJoin,
    handleLeave,
  } = useEventRSVP(event);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const handleJoinLeave = () => {
    if (isAttending || isWaitlisted) {
      setShowLeaveModal(true);
    } else {
      handleJoin();
    }
  };

  const handleConfirmLeave = () => {
    setShowLeaveModal(false);
    if (!userRsvp) return;
    handleLeave(userRsvp.ItemId);
  };

  const categories =
    categoriesData?.items
      .filter((category) => event.categoryIds?.includes(category.ItemId))
      ?.map((category) => category.categoryName) ?? [];

  const attendingCount = rsvpsData?.items?.length ?? 0;

  const { handleArchiveEvent, isEventArchived } = useEventArchive();
  const isArchived = isEventArchived(event.ItemId);

  const now = new Date();
  const coverImage = event.coverImage ?? undefined;
  const visibilityLabel = event.visibility ?? undefined;
  const hasParticipants = event.registrationPolicy !== "None";
  const capacity = event.capacity ?? 0;
  const attachments: Attachment[] = (event.attachmentUrls ?? []).map((url) => ({
    url,
    type: isImageFile(url) ? "image" : "file",
  }));
  const visibleAvatars = (rsvpsData?.items ?? [])
    .slice(0, MAX_VISIBLE_AVATARS)
    .flatMap((rsvp) =>
      rsvp.CreatedBy && rsvp.eventParticipantSnapshot?.profileImageUrl
        ? [
            {
              userId: rsvp.CreatedBy,
              profileImageUrl: rsvp.eventParticipantSnapshot.profileImageUrl,
            },
          ]
        : [],
    );
  const isOngoing =
    new Date(event.startTime) <= now && new Date(event.endTime) >= now;
  const isUpcoming = new Date(event.startTime) > now;
  const isCurrentUserAuthor = event.CreatedBy === user?.itemId;
  const showJoin = isUpcoming && event.registrationPolicy === "Required";

  const { mutate: deleteEvent, isPending: isDeleting } =
    useDeleteEventMutation();

  const handleEditEvent = useCallback((event: EventItem) => {
    router.push({
      pathname: "/events/[id]/edit",
      params: { id: event.ItemId },
    });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!eventToDelete) return;
    deleteEvent(eventToDelete.ItemId, {
      onSuccess: () => {
        setEventToDelete(null);
        router.replace("/events");
      },
    });
  }, [eventToDelete, deleteEvent]);
  return (
    <ScrollView
      className="flex-1"
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      <View className="rounded-2xl border border-ash-3 bg-chalk overflow-hidden p-4 gap-4">
        <BackNavigationButton
          label={t("LABEL_BACK")}
          iconSize={18}
          iconClassName="text-onyx"
          onPress={onBack}
          containerStyle={{ marginBottom: 0, alignSelf: "flex-start" }}
        />

        <EventCover
          uri={coverImage}
          title={event.title}
          visibilityLabel={visibilityLabel}
          categories={categories}
          isAuthor={event.CreatedBy === user?.itemId}
          author={event.eventOrganizerSnapshot ?? undefined}
          authorId={event.CreatedBy}
          attendingCount={attendingCount}
          visibleAvatars={visibleAvatars}
          isOngoing={isOngoing}
          isArchived={isArchived}
          showJoin={showJoin}
          onJoin={handleJoinLeave}
          joinButtonText={joinButtonText}
          isJoiningOrLeaving={isJoining || isLeaving}
          onParticipantsPress={
            hasParticipants ? () => setShowParticipants(true) : undefined
          }
          onEdit={() => handleEditEvent(event)}
          onArchive={() => handleArchiveEvent(event)}
          onDelete={() => setEventToDelete(event)}
          isAttending={isAttending}
          isWaitlisted={isWaitlisted}
        />

        {showParticipants ? (
          <EventParticipants
            event={event}
            isAuthor={isCurrentUserAuthor}
            onClose={() => setShowParticipants(false)}
          />
        ) : (
          <>
            <EventDetailInfo
              description={event.description}
              startTime={event.startTime}
              endTime={event.endTime}
              attendingCount={attendingCount}
              capacity={capacity}
              registrationPolicy={event.registrationPolicy}
            />
            {event.locationName && (
              <EventDetailLocation
                location={event.locationName}
                coordinates={{
                  lat: event.locationLat,
                  lng: event.locationLng,
                }}
              />
            )}
            {attachments.length > 0 && (
              <AttachmentViewer attachments={attachments} />
            )}
            {!isUpcoming && !isOngoing && (
              <EventDetailPhotos
                eventId={event.ItemId}
                isOrganizer={isCurrentUserAuthor}
              />
            )}
          </>
        )}
      </View>
      <ConfirmModal
        visible={!!eventToDelete}
        title="LABEL_DELETE_EVENT"
        description="QUEST_DELETE_EVENT"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setEventToDelete(null)}
      />
      <ConfirmModal
        visible={showLeaveModal}
        title={
          isWaitlisted
            ? "LABEL_LEAVE_WAITLIST_CONFIRM"
            : "LABEL_LEAVE_EVENT_CONFIRM"
        }
        description={
          isWaitlisted
            ? "HINT_LEAVE_WAITLIST_CONFIRM"
            : "HINT_LEAVE_EVENT_CONFIRM"
        }
        confirmText={t(isWaitlisted ? "BTN_LEAVE_WAITLIST" : "BTN_LEAVE")}
        cancelText={t("BTN_CANCEL")}
        onCancel={() => setShowLeaveModal(false)}
        onConfirm={handleConfirmLeave}
      />
    </ScrollView>
  );
};

export default EventDetail;
